import moment from "moment";
import { contributionConstants, userConstants } from "actions/types";
import env from "@beam-australia/react-env";
import { filter, get, map, pick } from "lodash";

import { kellyPlanId } from "statics/enterprisePlanIds";
import type { UserState } from "types/state";
import type {
  PaychexWorkerRecord,
  PaychexSplitContribution,
} from "types/models/paychexWorkerRecord";
import type { PaychexUserLinkType } from "statics/paychexUserLinkTypes";

interface UserAction {
  type: string;
  data?: unknown;
  isLoading?: boolean;
}

const initialState: UserState = {
  authenticated: false,
  profile: {},
  userState: {},
  companyState: {},
  companies: [],
  confirmEmailSuccess: false,
  initialUserLoading: true,
  id: null,
  userRoleId: null,
  username: null,
  emailConfirmed: false,
  auth0User: {},
  primaryNeededYellowPathDocs: [],
  secondaryNeededYellowPathDocs: [],
  tags: [],
  pendingEmailChangeRequest: {},
  employerLinkRequests: [],
  linkedEmployers: [],
  paychexWorkerRecords: [],
  selectedPaychexWorkerRecord: null,
  paychexLinkType: null,
  paycheckSplitContribution: null,
  investmentProfile: {},
  contributionAbility: {},
  requiredUpdates: {},
  featureFlags: [],
  rolloverInfo: {},
  userRegistered: false,
  userAgreements: [],
};

const profileFields = [
  "legalName",
  "firstName",
  "lastName",
  "address1",
  "address2",
  "city",
  "state",
  "postalCode",
  "phoneNumber",
  "email",
  "dob",
];

export function user(
  state: UserState = initialState,
  action: UserAction
): UserState {
  switch (action.type) {
    case userConstants.USER_STATE_FAILURE:
      return {
        ...state,
        authenticated: false,
        userState: {},
      };
    case userConstants.USER_STATE_SUCCESS: {
      const data = action.data as {
        userState: object;
        companyState: object;
        companies: unknown[];
        featureFlags: unknown[];
        paychexLinkType?: PaychexUserLinkType | null;
        user: object;
      };
      return {
        ...state,
        authenticated: true,
        userState: data.userState,
        companyState: data.companyState,
        companies: data.companies,
        featureFlags: data.featureFlags,
        paychexLinkType: data.paychexLinkType ?? null,
        userRegistered: true,
        ...data.user,
      };
    }
    case userConstants.CONFIRM_EMAIL_SUCCESS:
      return { ...state, confirmEmailSuccess: true };

    case userConstants.USER_KYC_COMPLETED_SUCCESS:
      return {
        ...state,
        userState: action.data as object,
      };

    case userConstants.USER_NEEDED_YELLOWPATH_DOCS_SUCCESS: {
      const data = action.data as {
        primaryNeededDocuments: unknown[];
        secondaryNeededDocuments: unknown[];
        tags: unknown[];
      };
      return {
        ...state,
        primaryNeededYellowPathDocs: data.primaryNeededDocuments,
        secondaryNeededYellowPathDocs: data.secondaryNeededDocuments,
        tags: data.tags,
      };
    }

    case userConstants.USER_LOGOUT_SUCCESS:
      return { ...state, ...initialState };
    case userConstants.SET_INITIAL_USER_LOADING:
      return {
        ...state,
        initialUserLoading: action.isLoading as boolean,
      };

    case userConstants.USER_PROFILE_SUCCESS: {
      const profile: UserState["profile"] = pick(
        action.data as Record<string, unknown>,
        profileFields
      );
      profile.userIsFiftyOrOlder = profile.dob
        ? moment().diff(moment(profile.dob), "years") >= 50
        : false;

      return {
        ...state,
        profile: {
          ...state.profile, // merge with existing profile
          ...profile,
        },
      };
    }

    case userConstants.USER_PROFILE_UPDATE_SUCCESS: {
      const profile: UserState["profile"] = pick(
        action.data as Record<string, unknown>,
        profileFields
      );
      profile.userIsFiftyOrOlder = profile.dob
        ? moment().diff(moment(profile.dob), "years") >= 50
        : false;

      return {
        ...state,
        profile: {
          ...state.profile, // merge with existing profile
          ...profile,
        },
      };
    }

    case userConstants.EMAIL_CHANGE_SUCCESS: {
      const data = action.data as { toEmail?: string } & object;
      return {
        ...state,
        auth0User: {
          ...state.auth0User,
          ["https://" + env("AUTH0_DOMAIN") + "/change_email"]: data.toEmail,
        },
        pendingEmailChangeRequest: data,
      };
    }

    case userConstants.GET_EMAIL_CHANGE_SUCCESS:
      return {
        ...state,
        pendingEmailChangeRequest: action.data as object,
      };

    case userConstants.DELETE_EMAIL_CHANGE_SUCCESS:
      return {
        ...state,
        pendingEmailChangeRequest: {},
        auth0User: {
          ...state.auth0User,
          ["https://" + env("AUTH0_DOMAIN") + "/change_email"]: null,
        },
      };
    case userConstants.USER_EMPLOYER_LINK_REQUESTS_SUCCESS: {
      const linkRequests = map(
        action.data as Array<Record<string, unknown>>,
        (linkRequest) => {
          const planId = get(linkRequest, "company.planId");
          const isKelly = planId === kellyPlanId;

          return { ...linkRequest, isKelly };
        }
      );
      return {
        ...state,
        employerLinkRequests: linkRequests,
      };
    }
    case userConstants.USER_ACCEPT_INVITATION_SUCCESS: {
      const planId = get(action.data, "company.planId");
      const isKelly = planId === kellyPlanId;

      return {
        ...state,
        employerLinkRequests: [
          ...state.employerLinkRequests,
          { ...(action.data as Record<string, unknown>), isKelly },
        ],
      };
    }

    case userConstants.USER_EMPLOYER_LINK_SUCCESS:
      return {
        ...state,
        linkedEmployers: map(
          action.data as Array<
            {
              groupWithSchedule: { group: unknown };
              schedule?: unknown;
            } & Record<string, unknown>
          >,
          (data) => ({
            ...data,
            group: data.groupWithSchedule.group,
            schedule: data.schedule,
          })
        ),
      };

    case userConstants.DELETE_USER_EMPLOYER_LINK_SUCCESS:
      return {
        ...state,
        linkedEmployers: filter(
          state.linkedEmployers,
          (link) => link.id !== action.data
        ),
      };

    case userConstants.PAYCHEX_WORKER_RECORDS_SUCCESS:
      return {
        ...state,
        paychexWorkerRecords: (action.data as PaychexWorkerRecord[]) ?? [],
      };

    case userConstants.PAYCHEX_WORKER_RECORD_SUCCESS:
      return {
        ...state,
        selectedPaychexWorkerRecord:
          (action.data as PaychexWorkerRecord) ?? null,
      };

    case userConstants.GET_PAYCHECK_SPLIT_CONTRIBUTION_SUCCESS:
      return {
        ...state,
        paycheckSplitContribution:
          (action.data as PaychexSplitContribution) ?? null,
      };

    case userConstants.GET_INVESTMENT_PROFILE_SUCCESS:
      return {
        ...state,
        investmentProfile: action.data as object,
      };

    case userConstants.DELETE_USER_EMPLOYER_LINK_REQ_SUCCESS:
      return {
        ...state,
        employerLinkRequests: action.data as Array<Record<string, unknown>>,
      };

    case contributionConstants.GET_CONTRIBUTION_ABILITY_SUCCESS:
      return {
        ...state,
        contributionAbility: action.data as object,
      };

    case userConstants.GET_USER_REQUIRED_STEPS_SUCCESS: {
      const data = action.data as { updates?: unknown[] };
      return {
        ...state,
        requiredUpdates: {
          updates: data.updates || [],
          currentUpdateIndex: 0,
        },
      };
    }

    case userConstants.UPDATE_REQUIRED_DISCLOSURES_SUCCESS: {
      const updated = action.data as { id?: unknown; isCompleted?: boolean };
      const existing = get(state, "requiredUpdates.updates", []) as Array<{
        id?: unknown;
        isCompleted?: boolean;
      }>;
      const nextUpdates = filter(
        map(existing, (u) => (u.id === updated.id ? updated : u)),
        (u) => !u.isCompleted
      );
      return {
        ...state,
        requiredUpdates: {
          ...state.requiredUpdates,
          updates: nextUpdates,
        },
      };
    }

    // pre-existing dead code: COMPLETE_ACCOUNT_UPDATES is not defined in
    // actions/types.ts and nothing dispatches it — in JS this case compared
    // against undefined and could never match. Preserved as-is (cast keeps
    // the compiler honest); recorded in the migration doc for cleanup.
    case (userConstants as Record<string, string | undefined>)
      .COMPLETE_ACCOUNT_UPDATES:
      return {
        ...state,
        requiredUpdates: {
          ...state.requiredUpdates,
          currentUpdateIndex:
            (state.requiredUpdates.currentUpdateIndex as number) + 1,
        },
      };

    case userConstants.REGISTER_OAUTH_USER_SUCCESS: {
      return {
        ...state,
        userRegistered: true,
      };
    }

    case userConstants.LINK_SECONDARY_AUTH_SUCCESS: {
      return {
        ...state,
        user: {
          ...state.user,
          userAuthorizations: action.data,
        },
      };
    }

    case userConstants.GET_USER_AGREEMENTS_SUCCESS: {
      return {
        ...state,
        userAgreements: action.data as unknown[],
      };
    }
    default:
      return state;
  }
}
